import { IntlShape } from "react-intl";
import produce from "immer";
import { TableColumnInputType } from "src/requests/table/types";
import {
  localizeOptionBlueprint,
  makeOptionCreatorForBlueprint,
} from "../../../../utils/options";
import { Option } from "../../../atoms/inputs/FlowioSelect/types";
import { Dictionary, RelationType } from "../../../../globalTypes";
import { WhereItemOptionsConfig } from "../types";

export const FLOWIO_LOGIN_TYPES = [
  RelationType.EwaLogin,
  RelationType.EwaUsername,
];

const HEG_LOGIN_TYPES = [RelationType.HegLogin, RelationType.HegUsername];

export const LOGIN_TYPES = [...FLOWIO_LOGIN_TYPES, ...HEG_LOGIN_TYPES];

const getUnfilteredBlueprint = (
  intl: IntlShape
): Record<RelationType, string> =>
  localizeOptionBlueprint(intl, {
    [RelationType.TextValue]: "whereItem.relationType.textValue",
    [RelationType.NumberValue]: "whereItem.relationType.numberValue",
    [RelationType.BooleanValue]: "whereItem.relationType.booleanValue",
    [RelationType.DateValue]: "whereItem.relationType.dateValue",
    [RelationType.DateValueRange]: "whereItem.relationType.dateValueRange",
    [RelationType.DateValueDynamic]: "whereItem.relationType.dateValueDynamic",
    [RelationType.Like]: "whereItem.relationType.like",
    [RelationType.Null]: "whereItem.relationType.isnull",
    [RelationType.DynamicValue]: "whereItem.relationType.dynamicValue",
    [RelationType.SelectValue]: "whereItem.relationType.selectValue",
    [RelationType.EwaLogin]: "whereItem.relationType.flowioLogin",
    [RelationType.EwaUsername]: "whereItem.relationType.flowioUsername",
    [RelationType.HegLogin]: "whereItem.relationType.hegLogin",
    [RelationType.HegUsername]: "whereItem.relationType.hegUsername",
  });

const getAcceptableRelationTypesForInputType = (
  inputType: TableColumnInputType,
  isSelect: boolean
): RelationType[] => {
  if (isSelect) return [RelationType.Null, RelationType.SelectValue];

  switch (inputType) {
    case TableColumnInputType.Number:
    case TableColumnInputType.Decimal:
      return [
        RelationType.Null,
        RelationType.DynamicValue,
        RelationType.NumberValue,
      ];

    case TableColumnInputType.Text:
    case TableColumnInputType.Textarea:
      return [
        ...LOGIN_TYPES,
        RelationType.Null,
        RelationType.DynamicValue,
        RelationType.TextValue,
        RelationType.Like,
      ];

    case TableColumnInputType.Link:
      return [
        RelationType.Null,
        RelationType.DynamicValue,
        RelationType.TextValue,
        RelationType.Like,
      ];

    case TableColumnInputType.Checkbox:
      return [
        RelationType.Null,
        RelationType.DynamicValue,
        RelationType.BooleanValue,
      ];

    case TableColumnInputType.DateFrom:
    case TableColumnInputType.DateTo:
    case TableColumnInputType.DatetimeLocal:
      return [
        RelationType.DateValue,
        RelationType.DateValueDynamic,
        RelationType.DateValueRange,
        RelationType.DynamicValue,
        RelationType.Null,
      ];

    case TableColumnInputType.File:
    case TableColumnInputType.HegFile:
      return [RelationType.Null, RelationType.DynamicValue];

    case TableColumnInputType.Hidden:
      return [RelationType.Null, RelationType.DynamicValue];

    case TableColumnInputType.OverviewSelect:
    case TableColumnInputType.Select:
      return [
        RelationType.Null,
        RelationType.TextValue,
        RelationType.DynamicValue,
      ];

    default:
      return [] as never;
  }
};

export const isRelationTypeViableForInputType = (
  relationType: RelationType,
  inputType: TableColumnInputType,
  isSelect: boolean
) => {
  const viableTypes = getAcceptableRelationTypesForInputType(
    inputType,
    isSelect
  );

  return viableTypes.includes(relationType);
};

const applyConfig = (
  blueprint: Dictionary<RelationType, string>,
  optionsConfig?: WhereItemOptionsConfig
): Dictionary<RelationType, string> =>
  produce(blueprint, (draft) => {
    if (optionsConfig?.allowLoginTypes && optionsConfig.flowioLoginOnly) {
      HEG_LOGIN_TYPES.forEach((t) => delete draft[t]);
    }

    if (!optionsConfig?.allowLoginTypes) {
      LOGIN_TYPES.forEach((t) => delete draft[t]);
    }

    if (!optionsConfig?.allowRelation) {
      delete draft[RelationType.DynamicValue];
    }
  });

export const getRelationTypeOptions = (
  intl: IntlShape,
  unformattedInputType: TableColumnInputType,
  isSelect: boolean,
  optionsConfig?: WhereItemOptionsConfig
) => {
  const unfilteredOptionsBlueprint = getUnfilteredBlueprint(intl);
  const blueprint = applyConfig(unfilteredOptionsBlueprint, optionsConfig);
  const createBpOptions = makeOptionCreatorForBlueprint(blueprint);

  return createBpOptions(
    getAcceptableRelationTypesForInputType(unformattedInputType, isSelect)
  );
};

const isOptionLoginType = (option: Option): boolean =>
  LOGIN_TYPES.includes(option.value as typeof LOGIN_TYPES[number]);

export const removeLoginTypes = (options: Option[]): Option[] =>
  options.filter((o) => !isOptionLoginType(o));
